/**
 * ENHANCED EFFECTS' gunfire: a modern tracer — a hot head with a glowing tail
 * lying along the round's **world** velocity — in place of the authentic
 * `BULLETS.BMP` dash.
 *
 * Pure geometry, no three.js, for the reason `bullet-sprites.ts` is: the pool
 * lifecycle, the tail basis and the near-plane clamp are all arithmetic and all
 * testable without a GPU. `three/tracer-pool.ts` owns the two `InstancedMesh`es
 * that consume the result.
 *
 * ## Why this exists — the measurement, and what it refuted
 *
 * The report was "see-saw sprites rather than proper bullets with tracers", and
 * the standing hypothesis was that the sheet's few discrete streak orientations
 * make a round's drawn angle *snap* between frames. **Measured, that is not what
 * happens.** `bullet-sprites.ts` uses three frames (`BULLETS.BMP` 0, 2 and 4),
 * all of them horizontal dashes, and rolls them by a continuously-computed
 * angle; driven through five firing passes (straight, gentle turn, hard turn,
 * rolling attack, cockpit and chase) the *visible* frame-to-frame swing of a
 * round's streak — mod 180°, because a dash is symmetric under a half turn — is
 * **0.00° per frame, p95 0.00°, max 0.00°**, and the angular spread across the
 * simultaneous rounds of one stream is **0.0°**. The only non-zero reading in
 * the whole sweep is 1.50°/frame under a 90°/s roll, which is the camera's own
 * roll and is correct. There is no snapping and no see-saw in the angle.
 *
 * What the same sweep *does* show is why a round reads as a sprite rather than
 * as a bullet. In every realistic firing geometry the streak's foreshortening
 * measures **0.001 to 0.06** — a round fired at something ahead of you is
 * flying very nearly down your own line of sight — which is far below
 * `bulletStreakScale`'s `1 / framePx` floor, so the floor is what is used
 * **always**. Every round of every burst is therefore drawn at exactly one
 * original pixel tall and its frame's aspect long: a **4 × 1 pixel dash**,
 * whatever the round is doing. That is faithful (the header says so, and the
 * ladder's three lengths — 4, 2⅔, 4 — are stated there), and it is also
 * precisely a see-saw sprite. A tracer is not a mark of ink; it is a light with
 * a tail, and a light needs geometry the authentic path deliberately does not
 * have.
 *
 * So this is a **replacement**, not a correction: `bullet-sprites.ts` is
 * untouched and draws exactly the pixels it drew when the toggle is off, and
 * this draws instead of it when the toggle is on — the same rule
 * `effects-settings.ts` states for aeroplane fire, smoke and the train plume.
 *
 * ## The one-in-three belt is already honoured, upstream
 *
 * `packages/engine`'s `gunnery.ts` marks one round in `gunTracerInterval` as a
 * tracer and `encounter-setup.ts`'s `roundViews` drops every round that is not
 * one, so **only tracers ever reach a renderer at all** — the non-tracer rounds
 * are not near-invisible, they are absent, which is what makes a stream read as
 * a stream. Nothing here re-implements or re-filters that; the pool below is
 * fed the same list the authentic path is fed and the ratio is whatever the
 * engine put in the air.
 *
 * ## Bombs are not tracers
 *
 * `roundViews` also appends the bombs in flight, deliberately, so a raid is
 * drawn from the record the crater was resolved from. A falling bomb is not
 * incandescent, so it stays on the authentic pixel-projectile path in **both**
 * toggle states (`RoundView.bomb`), and this pool skips it.
 *
 * ## The tail is a stretched billboard, which is what makes foreshortening free
 *
 * A tail is a quad that **contains the round's world direction** and is rolled
 * about it to be as face-on to the camera as that constraint allows
 * (`tracerTailBasis`). Its length is a length in metres, so the projection does
 * the foreshortening itself and nothing has to model it: a round crossing your
 * view draws its full tail, and one fired straight at or straight away from you
 * projects that tail to nothing and reads as the head alone — which is exactly
 * what a tracer coming at you looks like. That is the same quantity
 * `bulletStreak` measures on the CPU and then has to floor away; here it is
 * never computed, because the perspective divide already knows it.
 *
 * The tail's **width** and the head's **size** are world sizes too, clamped
 * between an apparent floor and ceiling rather than pinned to a constant
 * apparent size — see `TRACER_HEAD_DIAMETER_M` for the swapchain measurement
 * that decided it, which is the one place this pool departs from the port's
 * 1:1 blit rule and the reason a burst fired straight ahead reads as a stream
 * rather than as a single dot.
 */

import { MAX_LIVE_ROUNDS, type Vec3 } from "@red-opus/engine";
import { metresPerScreenPixel } from "../three/sprite-screen-size.ts";
import { muzzleShiftFor, type CameraBasis } from "./bullet-sprites.ts";
import type { RoundView } from "./encounter-setup.ts";
import { tracerTierFor, type TracerTier } from "./tracer-tint.ts";

/**
 * How long a tracer's glowing tail is, in metres of world space.
 *
 * A round leaves the muzzle near 720 m/s, so this is about 36 ms of flight —
 * roughly the persistence a tracer's burn leaves on the eye, and the reason a
 * real burst reads as a dotted line rather than a solid one. It is also short
 * against the spacing the belt produces: at 600 rpm with one round in three
 * lit, consecutive tracers are ~240 m apart, so a 26 m tail leaves nine parts
 * gap to one part light and the stream stays discrete.
 */
export const TRACER_TAIL_LENGTH_M = 26;

/**
 * The head glow's diameter **in metres**, and the readability argument that
 * decided it should be metres at all.
 *
 * The port's sizing rule is a constant *apparent* size
 * (`three/sprite-screen-size.ts`), and this pool was first written that way —
 * which failed on the swapchain, in the one case that matters most. Firing
 * straight ahead from level flight, every round of a burst is on very nearly
 * the same ray from the eye, so a constant apparent size draws them all at the
 * same place *and the same size*: measured in the cockpit at a head-on pass,
 * an entire burst rendered as **one dot in the middle of the gunsight ring**.
 * Nothing was wrong with the geometry — the rounds were all exactly where they
 * should be — but a stream with no size ladder along it carries no depth, and
 * with no depth there is no stream.
 *
 * A tracer is a burning object at a distance, not a blitted sprite, so its
 * glow gets a world size and the perspective divide does the rest: the round
 * just off the muzzle is a fat blob, the one at 300 m is a speck, and a burst
 * fired down its own line of sight reads as a receding cone of them. That is
 * the *only* place this pool departs from the 1:1 blit rule, and it is a
 * departure the authentic path could not make — a `BULLETS.BMP` frame is
 * blitted art and blitted art has no world size.
 *
 * A metre and a half is not the diameter of a bullet. It is the diameter of the
 * *bloom* a burning tracer element leaves on an eye or a lens, which is what is
 * actually being drawn and is what makes one visible at 300 m at all. The value
 * was set by where the ladder has to work rather than by a physical claim: at
 * this port's field of view a round subtends `192 · d⁻¹` original pixels per
 * metre of world size, so 1.6 m puts a tracer at **10 px at 30 m, 3 px at
 * 100 m and 1.5 px at 200 m** — a visible ramp across exactly the band a burst
 * lives in. The first attempt at 0.85 m sat on its own floor from ~110 m out,
 * which is most of a burst, and drew that whole burst at one size again.
 */
export const TRACER_HEAD_DIAMETER_M = 1.6;

/**
 * The apparent-size clamps on that world diameter, in original 200-line pixels.
 *
 * The floor is what stops a distant tracer thinning to a sub-pixel flicker —
 * past ~120 m the world glow is under two pixels and the ladder has said all it
 * can, so from there out it holds. The ceiling is `effect-sprites.ts`'s
 * `LENS_CLEARANCE_M` argument in another form: a round that leaves the muzzle
 * three metres from the pilot's eye would otherwise subtend a third of the
 * frame, and an effect the camera is inside is not an effect, it is a fill.
 */
export const TRACER_HEAD_MIN_PX = 1.4;
export const TRACER_HEAD_MAX_PX = 14;

/** The tail ribbon's width in metres, on the same argument as the head's diameter. */
export const TRACER_TAIL_WIDTH_M = 0.7;
/** And the same pair of apparent-size clamps, a little tighter — a trail is thinner than its source. */
export const TRACER_TAIL_MIN_WIDTH_PX = 0.8;
export const TRACER_TAIL_MAX_WIDTH_PX = 7;

/**
 * How close to the lens a tail is allowed to reach.
 *
 * A round is spawned a few metres in front of the pilot's eye and its tail
 * extends *backwards*, i.e. toward and then behind the camera — and a quad
 * straddling the near plane does not clip gracefully, it smears across the
 * frame. `tracerTailLengthM` shortens the tail so its far end stops here
 * instead, which costs nothing visually (the part that would have been cut is
 * behind you) and removes the case entirely.
 */
export const TRACER_MIN_TAIL_DEPTH_M = 2;

/** One quad per lit round, same cap the engine already enforces on the sim side. */
export const MAX_TRACERS = MAX_LIVE_ROUNDS;

/**
 * One tracer, in world space, as the two instanced meshes want it: flat number
 * fields rather than vectors, so a burst mutates the pool and allocates
 * nothing.
 *
 * The three tail axes are **unit** vectors; the meshes scale them by
 * `tailLengthM` and `tailWidthM` when they compose the instance matrix, which
 * keeps this record independent of how either quad's geometry is built.
 */
export interface TracerBolt {
  /** The round itself — the hot end of the tail and the centre of the head glow. */
  headX: number;
  headY: number;
  headZ: number;
  /** Centre of the tail ribbon: half a tail behind the head, along the flight direction. */
  tailX: number;
  tailY: number;
  tailZ: number;
  /** The tail's long axis: the round's own unit direction of flight, pointing at the head. */
  alongX: number;
  alongY: number;
  alongZ: number;
  /** The tail's width axis — across the streak on screen. */
  acrossX: number;
  acrossY: number;
  acrossZ: number;
  /** The tail quad's normal: as close to the camera as a plane containing `along` gets. */
  normalX: number;
  normalY: number;
  normalZ: number;
  /** World length of the tail this frame, already clamped off the near plane. */
  tailLengthM: number;
  /** World width of the tail ribbon — a constant apparent thickness. */
  tailWidthM: number;
  /** World diameter of the head glow quad — a constant apparent size. */
  headSizeM: number;
  /**
   * Which side's stream this round belongs to — the one non-numeric field on
   * the record, and deliberately not a colour.
   *
   * The "flat number fields" rule above is about not allocating a vector per
   * round per frame; an interned string literal allocates nothing either. What
   * it buys is that this module stays *pure geometry and identity* and never
   * learns what a tier looks like: `tracer-tint.ts` owns the palette rungs —
   * the shared hot and cool ones and the three per-side mid ones alike — and
   * `three/tracer-pool.ts` maps this to them when it writes the instance
   * attribute.
   */
  tier: TracerTier;
}

export function createTracerPool(capacity: number = MAX_TRACERS): TracerBolt[] {
  return Array.from({ length: capacity }, () => ({
    headX: 0,
    headY: 0,
    headZ: 0,
    tailX: 0,
    tailY: 0,
    tailZ: 0,
    alongX: 0,
    alongY: 0,
    alongZ: 1,
    acrossX: 1,
    acrossY: 0,
    acrossZ: 0,
    normalX: 0,
    normalY: 1,
    normalZ: 0,
    tailLengthM: 0,
    tailWidthM: 0,
    headSizeM: 0,
    // The shipped ramp, so a slot nobody has filled is the colour tracers were
    // before this existed rather than a third thing.
    tier: "friendly",
  }));
}

/** The orthonormal frame a stretched billboard is built on: `along` is given, the other two are derived. */
export interface TracerTailBasis {
  readonly acrossX: number;
  readonly acrossY: number;
  readonly acrossZ: number;
  readonly normalX: number;
  readonly normalY: number;
  readonly normalZ: number;
}

/**
 * The two axes that turn a flight direction into a camera-facing ribbon.
 *
 * `normal` is the part of "toward the camera" that survives being made
 * perpendicular to the flight direction — the roll about `along` that presents
 * the ribbon's face rather than its edge — and `across` completes a
 * right-handed frame (`along × across = normal`, i.e. `across = normal ×
 * along`), which is the order `Matrix4.makeBasis` wants for a `PlaneGeometry`
 * whose own normal is `+Z`.
 *
 * **The degenerate case is the common one and it is safe.** A round fired
 * along your line of sight leaves nothing of "toward the camera" once the
 * flight direction is taken out, so the roll is undefined — but that is exactly
 * the round whose tail projects to a point, so *any* perpendicular gives the
 * same picture. `camera.right`, made perpendicular, is the fallback, and if
 * even that is parallel to the flight direction `camera.up` is. This differs
 * from `bulletStreak`'s degenerate branch in the way that matters: there the
 * ill-conditioned quantity was the *angle a visible dash was drawn at*, here it
 * is the roll of a ribbon that has no visible extent.
 */
export function tracerTailBasis(direction: Vec3, toCamera: Vec3, camera: CameraBasis): TracerTailBasis {
  const projected = perpendicularTo(direction, toCamera);
  const chosen =
    projected ?? perpendicularTo(direction, camera.right) ?? perpendicularTo(direction, camera.up) ?? FALLBACK_NORMAL;
  return {
    acrossX: chosen.y * direction.z - chosen.z * direction.y,
    acrossY: chosen.z * direction.x - chosen.x * direction.z,
    acrossZ: chosen.x * direction.y - chosen.y * direction.x,
    normalX: chosen.x,
    normalY: chosen.y,
    normalZ: chosen.z,
  };
}

/** Last-resort roll for a direction that is somehow parallel to every camera axis at once. */
const FALLBACK_NORMAL: Vec3 = { x: 0, y: 1, z: 0 };

/** Below this a Gram-Schmidt residual is noise rather than a direction, and the next fallback is taken. */
const DEGENERATE = 1e-4;

/**
 * A world size held between an apparent-size floor and ceiling.
 *
 * `metresPerPixel` is `metresPerScreenPixel` at the round's own depth, so
 * `minPx · metresPerPixel` is the world size that subtends `minPx` *there* —
 * which is what makes both clamps screen-space statements applied to a
 * world-space quantity, rather than two different sizing rules fighting.
 */
export function clampApparent(worldM: number, metresPerPixel: number, minPx: number, maxPx: number): number {
  return Math.min(maxPx * metresPerPixel, Math.max(minPx * metresPerPixel, worldM));
}

/** `v` with `axis`'s component removed and renormalised, or `undefined` when nothing survives. */
function perpendicularTo(axis: Vec3, v: Vec3): Vec3 | undefined {
  const axial = v.x * axis.x + v.y * axis.y + v.z * axis.z;
  const x = v.x - axial * axis.x;
  const y = v.y - axial * axis.y;
  const z = v.z - axial * axis.z;
  const length = Math.hypot(x, y, z);
  if (length < DEGENERATE) return undefined;
  return { x: x / length, y: y / length, z: z / length };
}

/**
 * How much of `TRACER_TAIL_LENGTH_M` this round may actually draw.
 *
 * `headDepthM` is the head's view-space depth and `axialRate` is `direction ·
 * camera.forward` — how fast depth grows per metre travelled — so the tail's far
 * end sits at `headDepthM − length · axialRate`. Only a round moving *away* from
 * the camera (`axialRate > 0`) has a tail that runs toward the lens, and only
 * that case can need shortening; a round crossing or approaching takes the full
 * length. Never negative.
 */
export function tracerTailLengthM(headDepthM: number, axialRate: number, travelledM = Infinity): number {
  // A tail is the fire the round has already laid down, so it cannot be longer
  // than the round has flown. Without this a round fired on this very tick
  // draws its full length backwards out of the muzzle — through the engine,
  // the cockpit and out behind the tail — which is what "the tracers originate
  // behind the plane" was (user, 2026-08-24). At ~720 m/s a round clears 26 m
  // in about two ticks, so this binds only at the muzzle and nowhere else.
  const grown = Math.min(TRACER_TAIL_LENGTH_M, Math.max(0, travelledM));
  if (axialRate <= 0) return grown;
  const room = (headDepthM - TRACER_MIN_TAIL_DEPTH_M) / axialRate;
  return Math.min(grown, Math.max(0, room));
}

/**
 * Fills `pool` in place from this frame's rounds and returns how many slots are
 * live, exactly as `fillBulletPool` does — same pool discipline, same muzzle
 * shift (`muzzleShiftFor` is imported rather than repeated, so a round leaves
 * the *same* drawn breech on both paths and the painted muzzle flash stays
 * coherent with whichever one is drawing), and the same "slots past the count
 * are dead" contract.
 *
 * Bombs and rounds at or behind the lens are skipped; everything else the
 * engine put in the air is drawn.
 */
export function fillTracerPool(
  pool: TracerBolt[],
  rounds: readonly RoundView[],
  breeches: readonly Vec3[],
  barrels: readonly Vec3[],
  camera: CameraBasis,
  fovDeg: number,
): number {
  const fovYRadians = (fovDeg * Math.PI) / 180;
  let count = 0;
  for (const round of rounds) {
    if (count >= pool.length) break;
    if (round.bomb) continue;
    const shift = muzzleShiftFor(round, breeches, barrels);
    const headX = round.position.x + shift.x;
    const headY = round.position.y + shift.y;
    const headZ = round.position.z + shift.z;
    const toCameraX = camera.position.x - headX;
    const toCameraY = camera.position.y - headY;
    const toCameraZ = camera.position.z - headZ;
    const depth = -(toCameraX * camera.forward.x + toCameraY * camera.forward.y + toCameraZ * camera.forward.z);
    if (depth <= TRACER_MIN_TAIL_DEPTH_M) continue;
    const direction = round.direction;
    const axialRate = direction.x * camera.forward.x + direction.y * camera.forward.y + direction.z * camera.forward.z;
    const length = tracerTailLengthM(depth, axialRate, round.travelledM);
    const basis = tracerTailBasis(direction, { x: toCameraX, y: toCameraY, z: toCameraZ }, camera);
    const metresPerPixel = metresPerScreenPixel(depth, fovYRadians);
    // The head sits *on* the round, with no depth bias separating it from the
    // tail ribbon it meets there. It needs none: neither material writes depth
    // (`three/tracer-pool.ts`), so the two simply both add rather than tying
    // for a depth the GPU would break by draw order.
    const slot = pool[count]!;
    slot.headX = headX;
    slot.headY = headY;
    slot.headZ = headZ;
    // The ribbon runs backwards from the head, so its centre is half a tail
    // *against* the flight direction — which is what puts the quad's `+x` end,
    // where the shader paints the hot end, on the round itself.
    slot.tailX = headX - direction.x * length * 0.5;
    slot.tailY = headY - direction.y * length * 0.5;
    slot.tailZ = headZ - direction.z * length * 0.5;
    slot.alongX = direction.x;
    slot.alongY = direction.y;
    slot.alongZ = direction.z;
    slot.acrossX = basis.acrossX;
    slot.acrossY = basis.acrossY;
    slot.acrossZ = basis.acrossZ;
    slot.normalX = basis.normalX;
    slot.normalY = basis.normalY;
    slot.normalZ = basis.normalZ;
    slot.tailLengthM = length;
    slot.tailWidthM = clampApparent(TRACER_TAIL_WIDTH_M, metresPerPixel, TRACER_TAIL_MIN_WIDTH_PX, TRACER_TAIL_MAX_WIDTH_PX);
    slot.headSizeM = clampApparent(TRACER_HEAD_DIAMETER_M, metresPerPixel, TRACER_HEAD_MIN_PX, TRACER_HEAD_MAX_PX);
    slot.tier = tracerTierFor(round);
    count += 1;
  }
  return count;
}
